import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';

import { formatoEje } from '#components/mobile/informes/graficas';
import { Segmentado } from '#components/mobile/ui/Segmentado';
import { color, num, suave } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';

import { mesesFuertes, pasoEje } from './fichaCalculos';
import type { HistoriaGasto } from './fichaCalculos';

export type VistaEvolucion = 'gasto' | 'saldo' | 'meses' | 'recibos';

// Serie única (H47): tinta neutra; el verde queda para la línea de Asignado.
const COLOR_GASTO = color.fg3;
const ALTO = 150;
const IZQ = 34;
const ABAJO = 20;
const ARRIBA = 16;
const ETIQUETA = { fontSize: 11, fontWeight: 600 } as const;

/** Ancho real del contenedor, para dibujar el SVG sin escalar el texto. */
function useAncho() {
  const ref = useRef<HTMLDivElement>(null);
  const [ancho, setAncho] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const medir = () => setAncho(el.clientWidth);
    medir();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(medir);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, ancho };
}

function mesCorto(
  mes: string,
  locale: Parameters<typeof monthUtils.format>[2],
) {
  return monthUtils.format(mes, 'MMM', locale).replace('.', '');
}

function Leyenda({ children }: { children: ReactNode }) {
  return (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        columnGap: 14,
        rowGap: 4,
        marginTop: 8,
      }}
    >
      {children}
    </View>
  );
}

function ItemLeyenda({
  marca,
  children,
}: {
  marca: ReactNode;
  children: ReactNode;
}) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
      {marca}
      <Text style={{ fontSize: 12, fontWeight: 600, color: color.fg2, ...num }}>
        {children}
      </Text>
    </View>
  );
}

const cuadro = (fondo: string) => (
  <View
    style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: fondo }}
  />
);
const raya = (trazo: string, discontinua = false) => (
  <svg width={16} height={6} aria-hidden>
    <line
      x1={0}
      y1={3}
      x2={16}
      y2={3}
      stroke={trazo}
      strokeWidth={2}
      strokeDasharray={discontinua ? '4 3' : undefined}
    />
  </svg>
);

/** Ejes horizontales con sus marcas (0, paso, 2·paso…). */
function Rejilla({
  ancho,
  maximo,
  y,
}: {
  ancho: number;
  maximo: number;
  y: (v: number) => number;
}) {
  const paso = pasoEje(maximo);
  const marcas: number[] = [];
  for (let v = 0; v <= maximo + 1; v += paso) {
    marcas.push(v);
  }
  return (
    <g>
      {marcas.map(v => (
        <g key={v}>
          <line
            x1={IZQ}
            x2={ancho}
            y1={y(v)}
            y2={y(v)}
            stroke={color.line}
            strokeWidth={1}
          />
          <text
            x={IZQ - 6}
            y={y(v) + 4}
            textAnchor="end"
            fill={color.fg3}
            style={ETIQUETA}
          >
            {formatoEje(v)}
          </text>
        </g>
      ))}
    </g>
  );
}

/** A · Gasto mensual: barras, línea de lo asignado y banda de lo habitual. */
export function GraficoGasto({
  meses,
  gasto,
  asignado,
  banda: bandaDatos,
  historia = 'suficiente',
  seleccionado = null,
  onSeleccionar,
}: {
  meses: readonly string[];
  gasto: readonly IntegerAmount[];
  asignado: readonly IntegerAmount[];
  banda: { min: number; max: number };
  /**
   * Historia de gasto de la categoría (`historiaGasto`): sin ninguna, una
   * línea y sin gráfica; con pocos meses, sin banda de lo habitual.
   */
  historia?: HistoriaGasto;
  /** Mes seleccionado (barra resaltada) y al tocar una barra. */
  seleccionado?: string | null;
  onSeleccionar?: (mes: string) => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const format = useFormat();
  const { ref, ancho } = useAncho();
  // Con menos de tres meses no hay «habitual» que pintar.
  const banda = historia === 'suficiente' ? bandaDatos : { min: 0, max: 0 };
  // El eje sale de los datos (sin un mínimo inventado); solo si todo es 0
  // (gasto de hace más de un año) se usa una escala de reserva.
  const mayor = Math.max(0, ...gasto, ...asignado, banda.max);
  const maximo = (mayor > 0 ? mayor : 1000) * 1.12;
  const paso = pasoEje(maximo);
  const tope = Math.ceil(maximo / paso) * paso;
  const y = (v: number) => ARRIBA + (ALTO - ARRIBA - ABAJO) * (1 - v / tope);
  const anchoUtil = Math.max(0, ancho - IZQ);
  const col = anchoUtil / Math.max(1, meses.length);
  const barra = Math.min(22, col * 0.6);
  const x = (i: number) => IZQ + col * i + col / 2;
  const ultimoAsignado = asignado[asignado.length - 1] ?? 0;
  const pasos = asignado
    .map(
      (v, i) =>
        `${i === 0 ? 'M' : 'L'}${IZQ + col * i} ${y(v)}H${IZQ + col * (i + 1)}`,
    )
    .join('');

  if (historia === 'vacia') {
    return (
      <Text
        data-testid="grafico-gasto-vacio"
        style={{ fontSize: 13, color: color.fg3, padding: '8px 0' }}
      >
        <Trans>No spending in this category yet.</Trans>
      </Text>
    );
  }

  return (
    <View>
      <div
        ref={ref}
        style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}
        data-testid="grafico-gasto"
      >
        {ancho > 0 && (
          <PrivacyFilter>
            <svg width={ancho} height={ALTO} role="img" aria-label={t('Spend')}>
              <Rejilla ancho={ancho} maximo={tope} y={y} />
              {banda.max > 0 && (
                <rect
                  x={IZQ}
                  width={anchoUtil}
                  y={y(banda.max)}
                  height={Math.max(1, y(banda.min) - y(banda.max))}
                  fill={color.surface3}
                  opacity={0.6}
                />
              )}
              {gasto.map((v, i) => {
                const alto = Math.max(v > 0 ? 2 : 3, y(0) - y(Math.max(0, v)));
                return (
                  <g key={meses[i]}>
                    <rect
                      x={x(i) - barra / 2}
                      y={y(0) - alto}
                      width={barra}
                      height={alto}
                      rx={3}
                      fill={v > 0 ? COLOR_GASTO : color.surface3}
                      opacity={
                        seleccionado && seleccionado !== meses[i] ? 0.4 : 1
                      }
                    />
                    {historia === 'suficiente' && v > banda.max && v > 0 && (
                      <text
                        x={x(i)}
                        y={y(v) - 4}
                        textAnchor="middle"
                        fill={color.fg2}
                        style={{ ...ETIQUETA, fontWeight: 800 }}
                      >
                        {formatoEje(v)}
                      </text>
                    )}
                  </g>
                );
              })}
              {asignado.some(v => v !== 0) && (
                <>
                  <path
                    d={pasos}
                    fill="none"
                    stroke={color.accent}
                    strokeWidth={2}
                  />
                  <text
                    x={ancho - 2}
                    y={y(ultimoAsignado) - 5}
                    textAnchor="end"
                    fill={color.accent}
                    style={{ ...ETIQUETA, fontWeight: 800 }}
                  >
                    {formatoEje(ultimoAsignado)}
                  </text>
                </>
              )}
              {onSeleccionar &&
                meses.map((mes, i) => (
                  <rect
                    key={`toque-${mes}`}
                    data-testid={`barra-${mes}`}
                    data-gasto={gasto[i]}
                    x={IZQ + col * i}
                    y={0}
                    width={col}
                    height={ALTO}
                    fill="transparent"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSeleccionar(mes)}
                  />
                ))}
              {meses.map((mes, i) => (
                <text
                  key={mes}
                  x={x(i)}
                  y={ALTO - 4}
                  textAnchor="middle"
                  fill={color.fg3}
                  style={ETIQUETA}
                >
                  {mesCorto(mes, locale).slice(0, 3)}
                </text>
              ))}
            </svg>
          </PrivacyFilter>
        )}
      </div>
      <Leyenda>
        <ItemLeyenda marca={cuadro(COLOR_GASTO)}>
          <Trans>Spend</Trans>
        </ItemLeyenda>
        <ItemLeyenda marca={raya(color.accent)}>
          <Trans>Assigned</Trans>
        </ItemLeyenda>
        {historia === 'suficiente' ? (
          <ItemLeyenda marca={cuadro(color.surface3)}>
            {t('Usual {{from}}–{{to}}', {
              from: format(banda.min, 'financial-no-decimals'),
              to: format(banda.max, 'financial-no-decimals'),
            })}
          </ItemLeyenda>
        ) : (
          <Text style={{ fontSize: 12, color: color.fg3 }}>
            <Trans>Few data yet</Trans>
          </Text>
        )}
      </Leyenda>
    </View>
  );
}

/**
 * B · Saldo hacia la meta: saldo real al final de cada mes, proyección
 * punteada a la cuota hasta la fecha y la meta como línea horizontal.
 */
export function GraficoSaldo({
  meses,
  saldo,
  meta,
  onSeleccionar,
}: {
  meses: readonly string[];
  saldo: readonly IntegerAmount[];
  meta: { importe: IntegerAmount; mes: string } | null;
  /** Al tocar un mes del saldo real. */
  onSeleccionar?: (mes: string) => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const format = useFormat();
  const { ref, ancho } = useAncho();
  const hoy = meses[meses.length - 1];
  const fin = meta && meta.mes > hoy ? meta.mes : hoy;
  const total = monthUtils.differenceInCalendarMonths(fin, meses[0]) + 1;
  const maximo = Math.max(1000, ...saldo, meta?.importe ?? 0) * 1.1;
  const minimo = Math.min(0, ...saldo);
  const paso = pasoEje(maximo);
  const tope = Math.ceil(maximo / paso) * paso;
  const y = (v: number) =>
    ARRIBA + (ALTO - ARRIBA - ABAJO) * (1 - (v - minimo) / (tope - minimo));
  const anchoUtil = Math.max(0, ancho - IZQ - 10);
  const x = (i: number) => IZQ + (anchoUtil * i) / Math.max(1, total - 1);
  const iHoy = meses.length - 1;
  const saldoHoy = saldo[iHoy] ?? 0;
  const linea = saldo
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i)} ${y(v)}`)
    .join('');
  const area = `${linea}L${x(iHoy)} ${y(Math.max(0, minimo))}L${x(0)} ${y(Math.max(0, minimo))}Z`;
  const etiquetas = [0, iHoy, ...(total - 1 > iHoy ? [total - 1] : [])];

  return (
    <View>
      <div
        ref={ref}
        style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}
        data-testid="grafico-saldo"
      >
        {ancho > 0 && (
          <PrivacyFilter>
            <svg
              width={ancho}
              height={ALTO}
              role="img"
              aria-label={t('Balance')}
            >
              <Rejilla ancho={ancho} maximo={tope} y={y} />
              {meta && (
                <line
                  x1={IZQ}
                  x2={ancho}
                  y1={y(meta.importe)}
                  y2={y(meta.importe)}
                  stroke={color.fg3}
                  strokeWidth={1.5}
                  strokeDasharray="5 4"
                />
              )}
              <line
                x1={x(iHoy)}
                x2={x(iHoy)}
                y1={ARRIBA - 6}
                y2={y(minimo)}
                stroke={color.line2}
                strokeWidth={1}
              />
              <path d={area} fill={suave(color.accent, 14)} />
              <path
                d={linea}
                fill="none"
                stroke={color.accent}
                strokeWidth={2}
                strokeLinejoin="round"
              />
              {meta && meta.mes > hoy && (
                <>
                  <line
                    x1={x(iHoy)}
                    y1={y(saldoHoy)}
                    x2={x(total - 1)}
                    y2={y(meta.importe)}
                    stroke={color.accent}
                    strokeWidth={2}
                    strokeDasharray="5 4"
                  />
                  <circle
                    cx={x(total - 1)}
                    cy={y(meta.importe)}
                    r={4.5}
                    fill={color.surface}
                    stroke={color.accent}
                    strokeWidth={2}
                  />
                </>
              )}
              <circle cx={x(iHoy)} cy={y(saldoHoy)} r={4} fill={color.accent} />
              {etiquetas.map(i => {
                const mes = monthUtils.addMonths(meses[0], i);
                return (
                  <text
                    key={i}
                    x={x(i)}
                    y={ALTO - 4}
                    textAnchor={
                      i === 0
                        ? 'start'
                        : i === iHoy && i !== total - 1
                          ? 'middle'
                          : 'end'
                    }
                    fill={i === iHoy ? color.fg2 : color.fg3}
                    style={ETIQUETA}
                  >
                    {monthUtils
                      .format(mes, "MMM ''yy", locale)
                      .replace('.', '')}
                  </text>
                );
              })}
              {onSeleccionar &&
                meses.map((mes, k) => {
                  const ancho1 = anchoUtil / Math.max(1, total - 1);
                  return (
                    <rect
                      key={`toque-${mes}`}
                      data-testid={`saldo-${mes}`}
                      x={x(k) - ancho1 / 2}
                      y={0}
                      width={ancho1}
                      height={ALTO}
                      fill="transparent"
                      style={{ cursor: 'pointer' }}
                      onClick={() => onSeleccionar(mes)}
                    />
                  );
                })}
            </svg>
          </PrivacyFilter>
        )}
      </div>
      <Leyenda>
        <ItemLeyenda marca={raya(color.accent)}>
          {t('Balance {{amount}}', { amount: format(saldoHoy, 'financial') })}
        </ItemLeyenda>
        {meta && meta.mes > hoy && (
          <ItemLeyenda marca={raya(color.accent, true)}>
            <Trans>Projection</Trans>
          </ItemLeyenda>
        )}
        {meta && (
          <ItemLeyenda marca={raya(color.fg3, true)}>
            {t('Goal {{amount}}', {
              amount: format(meta.importe, 'financial-no-decimals'),
            })}
          </ItemLeyenda>
        )}
      </Leyenda>
    </View>
  );
}

/** C · Mapa de estacionalidad: 12 meses × años (filas). */
export function MapaMeses({
  filas,
  valores,
  onSeleccionar,
}: {
  filas: readonly (readonly string[])[];
  valores: readonly (readonly IntegerAmount[])[];
  /** Al tocar una celda (su mes). */
  onSeleccionar?: (mes: string) => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const format = useFormat();
  const maximo = Math.max(0, ...valores.flat());
  const fuertes = mesesFuertes(valores);
  const ultima = filas[filas.length - 1] ?? [];
  const etiquetaFila = (fila: readonly string[]) =>
    `${fila[0].slice(2, 4)}-${fila[11].slice(2, 4)}`;

  return (
    <View data-testid="grafico-meses">
      <PrivacyFilter>
        <View
          style={{
            display: 'grid',
            gridTemplateColumns: '38px repeat(12, minmax(0, 1fr))',
            gap: 3,
            alignItems: 'center',
          }}
        >
          <View />
          {ultima.map(mes => (
            <Text
              key={mes}
              style={{ ...ETIQUETA, color: color.fg3, textAlign: 'center' }}
            >
              {mesCorto(mes, locale).slice(0, 3)}
            </Text>
          ))}
          {filas.map((fila, f) => (
            <View key={fila[0]} style={{ display: 'contents' }}>
              <Text style={{ ...ETIQUETA, color: color.fg2, ...num }}>
                {etiquetaFila(fila)}
              </Text>
              {fila.map((mes, i) => {
                const v = Math.max(0, valores[f]?.[i] ?? 0);
                const nivel = maximo > 0 ? v / maximo : 0;
                const fuerte = nivel >= 0.6;
                return (
                  <View
                    key={mes}
                    data-testid={`celda-${mes}`}
                    onClick={
                      onSeleccionar ? () => onSeleccionar(mes) : undefined
                    }
                    title={`${monthUtils.format(mes, 'MMMM yyyy', locale)}: ${format(v, 'financial')}`}
                    style={{
                      height: 30,
                      borderRadius: 6,
                      cursor: onSeleccionar ? 'pointer' : undefined,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor:
                        v > 0
                          ? suave(COLOR_GASTO, Math.round(22 + 78 * nivel))
                          : color.surface2,
                    }}
                  >
                    {fuerte && (
                      <Text
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          color: color.surface,
                          letterSpacing: '-0.03em',
                          ...num,
                        }}
                      >
                        {formatoEje(v)}
                      </Text>
                    )}
                  </View>
                );
              })}
            </View>
          ))}
        </View>
      </PrivacyFilter>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 8,
          gap: 8,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
          <Text style={{ fontSize: 12, color: color.fg3, ...num }}>
            {format(0, 'financial-no-decimals')}
          </Text>
          {[22, 45, 70, 100].map(p => (
            <View key={p}>{cuadro(suave(COLOR_GASTO, p))}</View>
          ))}
          <Text style={{ fontSize: 12, color: color.fg3, ...num }}>
            <PrivacyFilter>
              {format(maximo, 'financial-no-decimals')}
            </PrivacyFilter>
          </Text>
        </View>
        {fuertes && fuertes.parte >= 0.5 && (
          <Text style={{ fontSize: 12, fontWeight: 700, color: color.fg2 }}>
            {t('{{months}}: {{percent}} of the year', {
              months: fuertes.columnas
                .slice()
                .sort((a, b) => a - b)
                .map(c => mesCorto(ultima[c], locale).slice(0, 3))
                .join(' + '),
              percent: `${Math.round(fuertes.parte * 100)} %`,
            })}
          </Text>
        )}
      </View>
    </View>
  );
}

/**
 * «Gasto · Saldo · Meses» de la tarjeta «Evolución» (B8 · H12: `Segmentado`),
 * y «Recibos» delante en un pago fijo con Gavi (Altamar).
 */
export function ChipsEvolucion({
  vista,
  onChange,
  conRecibos = false,
}: {
  vista: VistaEvolucion;
  onChange: (vista: VistaEvolucion) => void;
  conRecibos?: boolean;
}) {
  const { t } = useTranslation();
  const opciones: [VistaEvolucion, string][] = [
    ...(conRecibos
      ? [['recibos', t('Receipts')] as [VistaEvolucion, string]]
      : []),
    ['gasto', t('Spend')],
    ['saldo', t('Balance')],
    ['meses', t('Months')],
  ];
  return (
    <Segmentado<VistaEvolucion>
      etiqueta={t('Evolution')}
      opciones={opciones.map(([valor, texto]) => ({ valor, texto }))}
      valor={vista}
      onChange={onChange}
      testId="evolucion"
    />
  );
}
